import { dedupeTabsById } from '../utils/layoutTabUtils';
import { EPHEMERAL_TAB_TYPES } from './layoutStoreTypes';
import type { LayoutState, TargetTabWorkspace } from './layoutStoreTypes';
import { pruneTabSectionsToTabIds } from './pruneTabSections';

function partializeTargetTabWorkspace({
  workspace,
}: {
  workspace: TargetTabWorkspace;
}): TargetTabWorkspace {
  const persistableTabs = dedupeTabsById({
    tabs: workspace.tabs.filter(
      (tab) => !EPHEMERAL_TAB_TYPES.includes(tab.type),
    ),
  });
  const persistableTabIds = new Set(persistableTabs.map((tab) => tab.id));
  const workspaceTabIds = new Set(workspace.tabs.map((tab) => tab.id));

  return {
    tabs: persistableTabs.map((tab) => ({ ...tab, isActive: false })),
    tabNavigationByTabId: Object.fromEntries(
      Object.entries(workspace.tabNavigationByTabId).filter(([tabId]) =>
        persistableTabIds.has(tabId),
      ),
    ),
    tabHistory: workspace.tabHistory.filter((tabId) =>
      persistableTabIds.has(tabId),
    ),
    activeMainContentTabId:
      workspace.activeMainContentTabId &&
      persistableTabIds.has(workspace.activeMainContentTabId)
        ? workspace.activeMainContentTabId
        : undefined,
    tabGroups: workspace.tabGroups
      .map((group) => ({
        ...group,
        tabIds: group.tabIds.filter((tabId) => persistableTabIds.has(tabId)),
      }))
      .filter((group) => group.tabIds.length > 0),
    tabOrder: workspace.tabOrder.filter(
      (tabId) => persistableTabIds.has(tabId) || !workspaceTabIds.has(tabId),
    ),
  };
}

export const partializeLayoutState = (state: LayoutState) => {
  const persistableTabs = dedupeTabsById({
    tabs: state.mainContentTabs.filter(
      (tab) => !EPHEMERAL_TAB_TYPES.includes(tab.type),
    ),
  });
  const persistableTabIds = new Set(persistableTabs.map((tab) => tab.id));
  const persistableTabNavigation = Object.fromEntries(
    Object.entries(state.tabNavigationByTabId).filter(([tabId]) =>
      persistableTabIds.has(tabId),
    ),
  );
  const savedPinnedTabs = Object.fromEntries(
    Object.entries(state.savedPinnedTabs).map(([targetScopeKey, workspace]) => [
      targetScopeKey,
      partializeTargetTabWorkspace({ workspace }),
    ]),
  );

  return {
    selectedSidebarTab: state.selectedSidebarTab,
    showSidePanels: state.showSidePanels,
    isDomainTreeCollapsed: state.isDomainTreeCollapsed,
    isChatCollapsed: state.isChatCollapsed,
    isReplayCollectionsCollapsed: state.isReplayCollectionsCollapsed,
    isAiSignalsKindNavCollapsed: state.isAiSignalsKindNavCollapsed,
    isSidebarMinimized: state.isSidebarMinimized,
    rightPanelView: state.rightPanelView,
    mainPanelSizes: state.mainPanelSizes,
    globalRequestsSplitterSizes: state.globalRequestsSplitterSizes,
    webSocketSplitterSizes: state.webSocketSplitterSizes,
    interactSplitterSizes: state.interactSplitterSizes,
    interceptSplitterSizes: state.interceptSplitterSizes,
    replaySplitterSizes: state.replaySplitterSizes,
    replayCollectionsPanelSizes: state.replayCollectionsPanelSizes,
    requestsContentSplitterSizes: state.requestsContentSplitterSizes,
    selectedRequestsContentId: state.selectedRequestsContentId,
    requestViewerSplitterSizes: state.requestViewerSplitterSizes,
    matchReplaceRequestsPreviewSplitterSizes:
      state.matchReplaceRequestsPreviewSplitterSizes,
    matchReplaceOriginalTransformedSplitterSizes:
      state.matchReplaceOriginalTransformedSplitterSizes,
    matchReplaceRulesEditorSplitterSizes:
      state.matchReplaceRulesEditorSplitterSizes,
    matchReplaceRulesPreviewSplitterSizes:
      state.matchReplaceRulesPreviewSplitterSizes,
    mainContentTabs: persistableTabs,
    activeMainContentTabId: persistableTabIds.has(
      state.activeMainContentTabId ?? '',
    )
      ? state.activeMainContentTabId
      : undefined,
    tabHistory: state.tabHistory.filter((id) => persistableTabIds.has(id)),
    tabNavigationByTabId: persistableTabNavigation,
    globalNavigation: state.globalNavigation,
    endpointTabs: state.endpointTabs,
    webPageTabs: state.webPageTabs,
    tabSections: pruneTabSectionsToTabIds({
      tabSections: state.tabSections,
      tabIds: persistableTabIds,
    }),
    chatTabs: state.chatTabs,
    activeChatTabId: state.activeChatTabId,
    tabGroups: state.tabGroups,
    tabGroupingSettings: state.tabGroupingSettings,
    savedPinnedTabs,
  };
};
